// eslint-disable-next-line import/no-extraneous-dependencies
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { deleteToken } from '@/services/tokenService';
// eslint-disable-next-line import/extensions, import/no-unresolved, import/no-cycle
import api from '@/plugins/axios';
import dexieDB from '@/services/dexieService';

const state = {
  user: null,
  client_default: null,
  onboarding: null,
  fingerprint: null,
  profiles: {
    master: 'isAdmin',
    admin: 'isAdmin',
    user: 'isUser',
    client: 'isClient',
    operational: 'isOperational'
  },
  terms: {
    dialog: false,
    request: false,
    confirmed: false,
    loading: false
  },
  membershipTerms: {
    dialog: false
  },
  shift_block_active: false,
  can_access_system: true,
  parameters: []
};

const getters = {
  user: (state) => state.user,
  terms: (state) => state.terms,
  membershipTerms: (state) => state.membershipTerms,
  client_default: (state) => state.client_default,
  access(state) {
    if (!state.user || !state.user.role) {
      return null;
    }
    return state.profiles[state.user.role.name];
  },
  onboarding: (state) => state.onboarding,
  shiftBlockActive: (state) => state.shift_block_active,
  canAccessSystem: (state) => state.can_access_system,
  parameters: (state) => state.parameters,
  fingerprint: (state) => state.fingerprint
};

const mutations = {
  SET_USER(state, user) {
    state.user = user;
  },
  SET_CLIENT_DEFAULT(state, client) {
    state.client_default = client;
  },
  REMOVE_USER(state) {
    state.user = null;
  },
  SET_ONBOARDING(state, onboarding) {
    state.onboarding = onboarding;
  },
  SET_WHATSAPP_MESSAGE(state, value) {
    const indexParameter = state.user.organization.organization_parameters.findIndex((parameter) => parameter.code === 'WHATSAPP_SHARE_TEXT');
    if (indexParameter >= 0) {
      state.user.organization.organization_parameters[indexParameter].value = value;
    } else {
      state.user.organization.organization_parameters.push({
        code: 'WHATSAPP_SHARE_TEXT',
        value
      });
    }
  },
  SET_TERMS_LOADING(state, loading) {
    state.terms.loading = loading;
  },
  REQUEST_TERMS(state) {
    state.terms = {
      dialog: true,
      request: true,
      loading: false,
      confirmed: false
    };
  },
  RESET_TERMS(state) {
    state.terms = {
      dialog: false,
      request: false,
      loading: false,
      confirmed: false
    };
  },
  CONFIRM_TERMS(state) {
    state.terms.confirmed = true;
  },
  CLOSE_TERMS(state) {
    state.terms.dialog = false;
  },
  OPEN_TERMS(state) {
    state.terms.dialog = true;
  },
  CLOSE_MEMBERSHIP_TERMS(state) {
    state.membershipTerms.dialog = false;
  },
  OPEN_MEMBERSHIP_TERMS(state) {
    state.membershipTerms.dialog = true;
  },
  SET_SHIFT_BLOCK_ACTIVE(state, value) {
    state.shift_block_active = value;
  },
  SET_CAN_ACCESS_SYSTEM(state, value) {
    state.can_access_system = value;
  },
  SET_PARAMETERS(state, parameters) {
    state.parameters = parameters;
  },
  SET_FINGERPRINT(state, fingerprint) {
    state.fingerprint = fingerprint;
  }
};

const actions = {
  getUser({ commit }) {
    const onesignalID = localStorage.getItem('onesignal_id') ? localStorage.getItem('onesignal_id') : null;

    return new Promise((resolve, reject) => {
      api
        .get('/user/logged', {
          params: {
            onesignal_id: onesignalID
          }
        })
        .then(({ data }) => {
          commit('SET_USER', data);

          if (data.shift_block_active) {
            localStorage.setItem('shift_block_active', true);
            localStorage.setItem(
              'can_access_system',
              JSON.stringify({
                can_access: data.can_access_system,
                last_time_checked: new Date()
              })
            );

            commit('SET_SHIFT_BLOCK_ACTIVE', true);
            commit('SET_CAN_ACCESS_SYSTEM', data.can_access_system);
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getParametersFromLocal({ commit }) {
    dexieDB.getParameters().then((params) => {
      commit('SET_PARAMETERS', params);
    });
  },
  getDefaultClient({ commit }) {
    return new Promise((resolve, reject) => {
      api
        .get('/client/default')
        .then(({ data }) => {
          commit('SET_CLIENT_DEFAULT', data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getOnboarding({ commit }) {
    return new Promise((resolve, reject) => {
      api
        .get('/onboarding')
        .then((response) => {
          commit('SET_ONBOARDING', response.data.onboarding);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async logout({ commit }) {
    await deleteToken();
    commit('REMOVE_USER');
  },
  setWhatsappMessage({ commit }, value) {
    commit('SET_WHATSAPP_MESSAGE', value);
  },
  acceptTerms({ commit }) {
    commit('SET_TERMS_LOADING', true);
    api
      .post('/user/terms/accept')
      .then(() => {
        commit('RESET_TERMS');
        commit('CONFIRM_TERMS');
      })
      .catch(() => {
        commit('SET_TERMS_LOADING', false);
      });
  },
  openTerms({ commit }) {
    commit('OPEN_TERMS');
  },
  closeTerms({ commit }) {
    commit('CLOSE_TERMS');
  },
  openMembershipTerms({ commit }) {
    commit('OPEN_MEMBERSHIP_TERMS');
  },
  closeMembershipTerms({ commit }) {
    commit('CLOSE_MEMBERSHIP_TERMS');
  },
  updateUserAccess({ commit }, canAccess) {
    localStorage.setItem('can_access_system', JSON.stringify({ can_access: canAccess, last_time_checked: new Date() }));
    commit('SET_CAN_ACCESS_SYSTEM', canAccess);
  },
  async loadFingerprint({ commit, state: s }) {
    if (s.fingerprint) return s.fingerprint;
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    commit('SET_FINGERPRINT', result.visitorId);
    return result.visitorId;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
